import { supabase } from './api.js'

// Generate unique IDs
const generateId = () => Math.random().toString(36).substring(2, 15)

// Game Room Management
export async function createGameRoom({ hostName, partyMode = false, maxPlayers = 4 }) {
  const roomId = generateId().toUpperCase()
  
  const { data, error } = await supabase
    .from('game_rooms')
    .insert([{
      id: roomId,
      host_name: hostName,
      party_mode: partyMode,
      max_players: maxPlayers,
      status: 'waiting',
      created_at: new Date().toISOString()
    }])
    .select()
    .single()
  
  if (error) throw error
  return data
}

export async function joinGameRoom(roomId, playerName, isSpectator = false) {
  // Check if room exists
  const { data: room, error: roomError } = await supabase
    .from('game_rooms')
    .select('*')
    .eq('id', roomId.toUpperCase())
    .single()
  
  if (roomError || !room) {
    throw new Error('Room not found')
  }
  
  if (room.status !== 'waiting') {
    throw new Error('Game already in progress')
  }
  
  // Check player count
  const { data: existingPlayers, error: countError } = await supabase
    .from('game_players')
    .select('id')
    .eq('room_id', roomId.toUpperCase())
    .eq('is_active', true)
  
  if (countError) throw countError
  
  if (existingPlayers.length >= room.max_players && !isSpectator) {
    throw new Error('Room is full')
  }
  
  // Add player
  const playerId = generateId()
  const { data: player, error } = await supabase
    .from('game_players')
    .insert([{
      id: playerId,
      room_id: roomId.toUpperCase(),
      name: playerName,
      is_spectator: isSpectator,
      is_active: true,
      score: 0,
      stage: 0,
      powers: [],
      ready: false,
      joined_at: new Date().toISOString()
    }])
    .select()
    .single()
  
  if (error) throw error
  return player
}

export async function leaveRoom(roomId, playerId) {
  const { error } = await supabase
    .from('game_players')
    .update({ is_active: false })
    .eq('id', playerId)
    .eq('room_id', roomId.toUpperCase())
  
  if (error) throw error
}

export async function setPlayerReady(roomId, playerId, ready) {
  const { error } = await supabase
    .from('game_players')
    .update({ ready })
    .eq('id', playerId)
    .eq('room_id', roomId.toUpperCase())
  
  if (error) throw error
}

export async function updatePlayerScore(roomId, playerId, { score, combo, powers, stageCompleted }) {
  const { error } = await supabase
    .from('game_players')
    .update({
      score,
      combo,
      powers,
      stage: stageCompleted,
      updated_at: new Date().toISOString()
    })
    .eq('id', playerId)
    .eq('room_id', roomId.toUpperCase())
  
  if (error) throw error
}

export async function startGame(roomId) {
  const { error } = await supabase
    .from('game_rooms')
    .update({ 
      status: 'playing',
      started_at: new Date().toISOString()
    })
    .eq('id', roomId.toUpperCase())
  
  if (error) throw error
}

export async function updateGameState(roomId, state) {
  const { error } = await supabase
    .from('game_rooms')
    .update({
      game_state: state,
      updated_at: new Date().toISOString()
    })
    .eq('id', roomId.toUpperCase())
  
  if (error) throw error
}

// Realtime Subscriptions
export function subscribeToRoom(roomId, callback) {
  const channel = supabase
    .channel(`room:${roomId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'game_players',
        filter: `room_id=eq.${roomId.toUpperCase()}`
      },
      (payload) => {
        callback(payload)
      }
    )
    .subscribe()
  
  return channel
}

export function subscribeToGameState(roomId, callback) {
  const channel = supabase
    .channel(`game:${roomId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'game_rooms',
        filter: `id=eq.${roomId.toUpperCase()}`
      },
      (payload) => {
        callback(payload.new)
      }
    )
    .subscribe()
  
  return channel
}

// Get room players
export async function getRoomPlayers(roomId) {
  const { data, error } = await supabase
    .from('game_players')
    .select('*')
    .eq('room_id', roomId.toUpperCase())
    .eq('is_active', true)
    .order('score', { ascending: false })
  
  if (error) throw error
  return data
}

// Get room info
export async function getRoomInfo(roomId) {
  const { data, error } = await supabase
    .from('game_rooms')
    .select('*')
    .eq('id', roomId.toUpperCase())
    .single()
  
  if (error) throw error
  return data
}

// Team management for party mode
export async function createTeam(roomId, teamName) {
  const teamId = generateId()
  const { data, error } = await supabase
    .from('game_teams')
    .insert([{
      id: teamId,
      room_id: roomId.toUpperCase(),
      name: teamName,
      score: 0,
      created_at: new Date().toISOString()
    }])
    .select()
    .single()
  
  if (error) throw error
  return data
}

export async function joinTeam(roomId, playerId, teamId) {
  const { error } = await supabase
    .from('game_players')
    .update({ team_id: teamId })
    .eq('id', playerId)
    .eq('room_id', roomId.toUpperCase())
  
  if (error) throw error
}

export async function updateTeamScore(roomId, teamId, score) {
  const { error } = await supabase
    .from('game_teams')
    .update({ score })
    .eq('id', teamId)
    .eq('room_id', roomId.toUpperCase())
  
  if (error) throw error
}

export async function getTeams(roomId) {
  const { data, error } = await supabase
    .from('game_teams')
    .select('*, players:game_players(*)')
    .eq('room_id', roomId.toUpperCase())
    .order('score', { ascending: false })
  
  if (error) throw error
  return data
}

// Spectator features
export async function getSpectatorData(roomId) {
  const [room, players] = await Promise.all([
    getRoomInfo(roomId),
    getRoomPlayers(roomId)
  ])
  
  return {
    room,
    players: players.filter(p => !p.is_spectator),
    spectators: players.filter(p => p.is_spectator)
  }
}

// Chat messages
export async function sendChatMessage(roomId, playerId, playerName, message) {
  const { error } = await supabase
    .from('game_chat')
    .insert([{
      room_id: roomId.toUpperCase(),
      player_id: playerId,
      player_name: playerName,
      message,
      created_at: new Date().toISOString()
    }])
  
  if (error) throw error
}

export function subscribeToChat(roomId, callback) {
  const channel = supabase
    .channel(`chat:${roomId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'game_chat',
        filter: `room_id=eq.${roomId.toUpperCase()}`
      },
      (payload) => {
        callback(payload.new)
      }
    )
    .subscribe()
  
  return channel
}

// End game and cleanup
export async function endGame(roomId) {
  const { error } = await supabase
    .from('game_rooms')
    .update({ 
      status: 'finished',
      ended_at: new Date().toISOString()
    })
    .eq('id', roomId.toUpperCase())
  
  if (error) throw error
}

export async function cleanupOldGames() {
  // Remove games older than 24 hours
  const cutoff = new Date()
  cutoff.setHours(cutoff.getHours() - 24)
  
  const { error } = await supabase
    .from('game_rooms')
    .delete()
    .lt('created_at', cutoff.toISOString())
  
  if (error) console.error('Cleanup error:', error)
}
